import {EventEmitter, Injectable, Output} from '@angular/core';
import {LogicheckCase} from '../domain/logicheck-case';
import {RestService} from './rest.service';
import {UtilService} from './util.service';
import {CaseStateService} from './case-state.service';
import {Subject} from 'rxjs';
import * as _ from 'lodash';
import {ClientCaseId} from '../domain/client-case-id';
import {ConfigService} from './config.service';
import {HttpClient} from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class LogicheckCaseService {

    public currentCase: LogicheckCase;
    public historyCase: LogicheckCase;
    private transferCase: LogicheckCase;
    private currentCaseType: string;
    private clientCaseIdValid: boolean;
    private clientCaseIdComplete: boolean;
    public logicheckCase = new Subject<any>();

    @Output() caseLoaded = new EventEmitter();
    @Output() historyCasesLoaded = new EventEmitter();
    @Output() caseArchived = new EventEmitter();
    @Output() reclamationFinished = new EventEmitter();
    @Output() transferToLogicheckCompleted = new EventEmitter();
    @Output() requestFailed = new EventEmitter();
    @Output() historyCaseOpened = new EventEmitter();
    @Output() displayLoadingIndicator = new EventEmitter();
    @Output() logicheckDataCleared = new EventEmitter();
    @Output() caseDeleted = new EventEmitter();
    @Output() transferToPiaCompleted = new EventEmitter();
    @Output() lossCompensationStatesUpdated = new EventEmitter();

    @Output() invalidClientCaseId = new EventEmitter();
    private processingSubmit = false;
    public clientCaseId: ClientCaseId;
    public company: string;

    public viewState: string;

    public blockHistoryDialog = false;

    private lossCompensationStatesLoaded = false;

    constructor(protected restService: RestService,
                protected caseStateService: CaseStateService,
                private configService: ConfigService,
                private httpClient: HttpClient) {
    }

    setClientCaseIdValid(valid: boolean) {
        this.clientCaseIdValid = valid;
    }

    setClientCaseIdComplete(complete: boolean) {
        this.clientCaseIdComplete = complete;
    }

    setCurrentCaseType(caseType: string) {
        this.currentCaseType = caseType;
        this.viewState = null;
        this.initCase();
    }

    setTransferCase(transferCase: LogicheckCase) {
        this.transferCase = transferCase;
    }

    getTransferCase() {
        return this.transferCase;
    }

    getTemplate(caseType?: string) {
        if (!caseType) {
            caseType = this.currentCaseType;
        }

        this.currentCase = null;
        const path = UtilService.camelCaseToDash(caseType);
        return this.restService.get<LogicheckCase>('/' + path + '/template');
    }

    initCase() {
        if (this.historyCase) {
            this.currentCase = _.cloneDeep(this.historyCase);
            this.viewState = this.caseStateService.proceedToNextState(this.currentCase.state);
            delete this.historyCase;
            if (sessionStorage.getItem('caseFillData')) {
                sessionStorage.removeItem('caseFillData');
            }
            this.caseLoaded.emit();
            this.logicheckCase.next();
            this.historyCaseOpened.emit();
        } else {
            const path = UtilService.camelCaseToDash(this.currentCaseType);
            if (this.transferCase) {
                this.currentCase = _.cloneDeep(this.transferCase);
                this.transferCase = null;
                if (sessionStorage.getItem('caseFillData')) {
                    sessionStorage.removeItem('caseFillData');
                }
                this.viewState = this.currentCase.state;
                this.caseLoaded.emit(this.transferCase);
                this.logicheckCase.next();
            } else {
                this.restService.get<LogicheckCase>('/' + path + '/template')
                    .then((data) => {
                        this.currentCase = _.cloneDeep(data);
                        const s = this.caseStateService.getStates();
                        if (s !== undefined && s.states !== undefined && s.states.length > 0) {
                            this.viewState = s.states[0];
                        }
                        if (this.clientCaseId) {
                            this.currentCase.clientCaseId = _.cloneDeep(this.clientCaseId);
                            if (!this.blockHistoryDialog) {
                                this.getCaseByClientCaseId(this.clientCaseId.plain, true);
                            } else {
                                this.blockHistoryDialog = false;
                            }
                            this.clientCaseId = null;
                        }
                        if (this.company) {
                            this.currentCase.company = this.company;
                            this.company = null;
                        }
                        if (sessionStorage.getItem('soapData')) {
                            this.currentCase.soapData = JSON.parse(sessionStorage.getItem('soapData'));
                            sessionStorage.removeItem('soapData');
                        }
                        this.caseLoaded.emit(data);
                        this.logicheckCase.next();
                    });
            }
        }
    }

    public sendCaseUpdatedEvent() {
        const s = this.caseStateService.getStates();
        if (s !== undefined && s.states !== undefined && s.states.length > 0) {
            this.viewState = s.states[0];
        }
        this.logicheckCase.next();
    }

    public transferData<T, U>(url: string, data: any, headers: any) {
        this.displayLoadingIndicator.emit();
        this.restService.post<T, U>(url, data, headers)
            .then((response) => {
                    this.transferToLogicheckCompleted.emit(true);
                },
                () => {
                    this.transferToLogicheckCompleted.emit(false);
                });
    }

    submitCase(viewState: string, finishAction?: string) {
        if (!this.processingSubmit) {
            if (!this.clientCaseIdValid || !this.clientCaseIdComplete || !this.currentCase.clientCaseId.plain) {
                this.invalidClientCaseId.emit();
                this.requestFailed.emit();
                return;
            }
            const path = UtilService.camelCaseToDash(this.currentCaseType);
            // client case id changed
            if (sessionStorage.getItem('clientCaseId')) {
                const clientCaseId = JSON.parse(sessionStorage.getItem('clientCaseId'));
                sessionStorage.removeItem('clientCaseId');
                if (this.currentCase.clientCaseId.plain !== clientCaseId.plain) {
                    this.currentCase.requestData.clientCaseId = _.cloneDeep(this.currentCase.clientCaseId);
                    this.currentCase.clientCaseId = clientCaseId;
                }
            }
            if (this.clientCaseIdValid && this.clientCaseIdComplete) {
                this.viewState = viewState;
                const objectToSend = _.cloneDeep(this.currentCase);
                if (this.caseStateService.getStateNo(this.viewState) >
                    this.caseStateService.getStateNo(this.currentCase.state)) {
                    objectToSend.state = viewState;
                }
                objectToSend.requestData.requestState = viewState;

                this.restService.post<LogicheckCase, LogicheckCase>('/' + path + '?id=' +
                    this.currentCase.clientCaseId.plain.replace(' ', '+'), objectToSend)
                    .then((responseCase) => {
                        this.processingSubmit = false;
                        if (Object.keys(responseCase.responseData.validationErrors).length === 0) {
                            this.currentCase = responseCase;

                            if (viewState !== null && viewState !== 'RESULT_DATA') {
                                const nextState = this.caseStateService.proceedToNextState(viewState);
                                if (this.caseStateService.getStateNo(nextState) >
                                    this.caseStateService.getStateNo(this.currentCase.state)) {
                                    this.viewState = nextState;
                                } else {
                                    this.viewState = this.caseStateService.proceedToNextState(this.currentCase.state);
                                }
                            }
                        } else {
                            const tmp = this.currentCase.state;
                            this.currentCase = responseCase;
                            this.currentCase.state = tmp;
                            this.viewState = viewState;
                        }
                        this.caseLoaded.emit();
                        this.logicheckCase.next();
                        if (finishAction !== undefined) {
                            switch (finishAction) {
                                case 'ARCHIVE':
                                    this.archiveCase(false);
                                    break;
                                case 'OPEN_PDF':
                                    this.openPdf();
                                    break;
                                case 'OPEN_PDF_ARCHIVE':
                                    this.archiveCase(true);
                                    break;
                                case 'FINISH_RECLAMATION':
                                    this.finishReclamation();
                                    break;
                            }
                        }
                    }, (error) => {
                        this.processingSubmit = false;
                        this.requestFailed.emit();
                    });
                this.processingSubmit = true;
            } else {

            }

        }
    }

    public postCase() {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        const objectToSend = _.cloneDeep(this.currentCase);
        objectToSend.state = 'RESULT_DATA';
        if (this.currentCaseType === 'MinimalRentalPriceCase') {
            this.restService.post<LogicheckCase, LogicheckCase>('/' + path + '?id=' +
                this.currentCase.clientCaseId.plain.replace(' ', '+'), objectToSend);
        }
    }

    /*public openCase(clientCaseId: string) {
        const path = this.restService.getCurrentApp() === 'kobra' ? 'tow-case-logicheck' : 'expert-calculation-case-logicheck';
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + path + '/' + clientCaseId;
        this.restService.get<LogicheckCase>(url).then((res) => {
            this.viewState = this.caseStateService.proceedToNextState(res.state);
            // res.state = this.caseStateService.proceedToNextState(res.state);
            this.setCurrentCase(res);
            this.caseLoaded.emit();
            this.logicheckCase.next();
        });
    }*/

    clearLogicheckData(clientCaseId: string) {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + path + '/' + clientCaseId + '/clear-logicheck-data';
        this.restService.get<any>(url).then(res => {
            this.logicheckDataCleared.emit(true);
        });
    }

    markLogicheckDataAsFinished(clientCaseId: string) {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + path + '/' + clientCaseId + '/mark-as-finished';
        this.restService.get<any>(url).then(res => {
        });
    }

    deleteCase(clientCaseId: string) {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + path + '/' + clientCaseId;
        this.restService.delete<any>(url).then(res => {
            this.caseDeleted.emit(true);
        });
    }

    transferToPia(clientCaseId: string) {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + path + '/' + clientCaseId + '/transfer-to-pia';
        this.restService.get<any>(url).then(res => {
            this.transferToPiaCompleted.emit(true);
        });
    }

    waitingForDocuments(clientCaseId: string, waitingForDocuments: boolean) {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + path + '/' + clientCaseId + '/waiting-for-documents/' + waitingForDocuments;
        return this.restService.get<any>(url);
    }



    getCaseByClientCaseId(clientCaseId: string, alternativeText?: boolean) {
        if (alternativeText == null) {
            alternativeText = false;
        }
        let path;
        switch (this.currentCaseType) {
            case 'TowCase':
            case 'MinimalTowCase':
                path = 'kobra-case';
                break;
            case 'RentalPriceCase':
            case 'LossCompensationCase':
            case 'PriceInfoCase':
            case 'PriceEstimationCase':
            case 'ReplacementVehicleCase':
            case 'MinimalRentalPriceCase':
            case 'CommercialVehicleCase':
                path = 'miwap-case';
                break;
            case 'ExpertCalculationCase':
            case 'MinimalExpertCalculationCase':
            case 'SpecialVehicleCase':
            case 'TeleExpertCase':
                path = 'horch-case';
                break;
        }
        if (clientCaseId) {
            clientCaseId = clientCaseId.replace('/', '%2F');
            this.restService.get<LogicheckCase[]>('/' + path + '/' + encodeURI(clientCaseId))
                .then((data) => {
                    this.historyCasesLoaded.emit({cases: data, alternativeText: alternativeText});
                }, () => {

                });
        }
    }

    setCurrentCase(logicheckCase: LogicheckCase) {
        this.currentCase = logicheckCase;
        if (logicheckCase) {
            this.caseStateService.setCaseType(logicheckCase.type);
            this.caseStateService.setCurrentState(logicheckCase.state);
            this.currentCaseType = logicheckCase.type;
            this.clientCaseIdComplete = true;
            this.clientCaseIdValid = true;
            this.logicheckCase.next();
            this.caseLoaded.emit();
        }
    }

    proceedToNextState() {
        const nextState = this.caseStateService.proceedToNextState(this.viewState);
        if (this.caseStateService.getStateNo(nextState) >
            this.caseStateService.getStateNo(this.currentCase.state)) {
            this.viewState = nextState;
            this.logicheckCase.next();
            this.caseLoaded.emit();
        }
    }

    public setViewState(state: string) {
        if (this.viewState !== state) {
            this.viewState = state;
            this.caseLoaded.emit();
        }
    }

    isClientCaseIdComplete() {
        return this.clientCaseIdComplete;
    }

    isClientCaseIdValid() {
        return this.clientCaseIdValid;
    }

    private archiveCase(openPdf: boolean) {
        const path = UtilService.camelCaseToDash(this.currentCaseType);
        const clientCaseId = encodeURI(this.currentCase.clientCaseId.plain.replace('/', '%2F'));

        this.restService.get<any>('/' + path + '/' +
            clientCaseId + '/archive').then(
            (res) => {
                if (res.ok) {
                    this.caseArchived.emit(true);
                    if (openPdf) {
                        setTimeout(
                            () => this.openPdf(), 1000);
                    }
                } else {
                    this.caseArchived.emit(false);
                    if (openPdf) {
                        setTimeout(
                            () => this.openPdf(), 1000);
                    }
                }
            },
            (error) => {
                this.caseArchived.emit(false);
                if (openPdf) {
                    setTimeout(
                        () => this.openPdf(), 1000);
                }
            }
        );

    }

    private finishReclamation() {
        const path = UtilService.camelCaseToDash(this.currentCaseType);

        const clientCaseId = encodeURI(this.currentCase.clientCaseId.plain.replace('/', '%2F'));

        this.restService.get<any>('/' + path + '/' +
            clientCaseId + '/finish-reclamation').then(
            (res) => {
                this.reclamationFinished.emit(true);
            },
            (error) => {
                this.reclamationFinished.next(false);
            }
        );
    }

    public openLogicheckAttachment(clientCaseId: ClientCaseId | any, docId?: string) {
        const urlPart = this.restService.getCurrentApp() === 'kobra' ? 'tow-case' : 'expert-calculation-case';
        let url = '/' + this.restService.getCurrentApp() + '/api/v1/' +
            urlPart + '/' + clientCaseId.plain + '/attached-file';
        if (docId) {
            url += '/' + docId;
        }
        this.getPdf(url, clientCaseId.plain);
    }

    public openPdf(logicheckCase?: LogicheckCase) {
        const caseToOpen = logicheckCase || this.currentCase;
        let addLogEntry = true;
        if (logicheckCase != null) {
            addLogEntry = false;
        }
        let clientCaseId = '';
        if (typeof caseToOpen.clientCaseId === 'string') {
            clientCaseId = caseToOpen.clientCaseId;
        } else {
            clientCaseId = caseToOpen.clientCaseId.plain;
        }
        const fileName = clientCaseId + '.pdf';
        const p = UtilService.camelCaseToDash(caseToOpen.type);
        clientCaseId = encodeURI(clientCaseId.replace('/', '%2F'));
        const url = '/' + this.restService.getCurrentApp() + '/api/v1/' +
            p + '/' + clientCaseId +
            '/pdf';
        this.getPdf(url, fileName, addLogEntry);
    }

    private getPdf(url: string, fileName: string, addLogEntry?: boolean) {
        let logEntry = true;

        if (addLogEntry != null) {
            logEntry = addLogEntry;
        }
        let finalUrl = url + '?';
        if (!logEntry) {
            finalUrl += 'log=false';
        }
        this.httpClient.get(finalUrl, {responseType: 'arraybuffer'}).subscribe((data) => {
            const byteArray = new Uint8Array(data);
            const blob = new Blob([byteArray], {type: 'application/pdf'});
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveOrOpenBlob(blob, fileName);
            } else {
                const objectUrl = URL.createObjectURL(blob);
                window.open(objectUrl);
            }

        });

        // window.open(finalUrl + 'token=' + this.jwtDecoder.getAuthToken(), '_blank');
    }

    public emitCaseLoadedEvent() {
        this.caseLoaded.emit();
    }


    initStatesForLossCompensationCase() {
        if (!this.lossCompensationStatesLoaded) {
            this.configService.getConfig('LossCompensationCase');
            const sub = this.configService.configLoaded.subscribe((data) => {
                this.lossCompensationStatesLoaded = true;
                sub.unsubscribe();
                if (data['enable_rental_duration'] !== undefined &&
                    data['enable_rental_duration'][0].value === true) {
                    const caseStates = this.caseStateService.getStatesForCaseType('LossCompensationCase');
                    if (caseStates.states.indexOf('RENTAL_DURATION') === -1) {
                        caseStates.states.splice(1, 0, 'RENTAL_DURATION');
                    }
                    this.lossCompensationStatesUpdated.emit();
                }
            });
        }

    }
}
